import {UNIT_DEATH_CAST} from '../configs/unitDeathAnimation';
import {resolveUnitRecruitmentTier} from '../configs/unitRecruitmentAnimation';
import {COMBAT_TYPE} from '../constants/combat';
import {finishUnitDeathAnimation, startUnitDeathAnimation} from '../redux/actions';
import {selectPlayers} from '../redux/reducers/sharedStateReducer/selectors';
import {buildUnitDeathCastPlan} from '../utils/effects/buildUnitDeathCast';
import {startUnitDeathPause} from '../utils/effects/unitDeathPause';
import {getPlayer0xColor} from '../utils/players/getPlayer0xColor';
import {checkIfAnimationAllowed} from './playAbilityCastEffects';

let modalReleaseTimeoutId = null;

// Погибшие на ловушке исчезли с поля еще на этапе перемещения: в итоги они попадают позже
const checkIfDiedAtStageEnd = ({combatType}) => combatType === COMBAT_TYPE.MELEE || combatType === COMBAT_TYPE.SHOOTING;

/** Виден ли отряд на этой ячейке: запас за край кадра — в долях ячейки */
export const checkIfUnitInView = (scene, {center: {x, y}, hexSize}, marginFactor) => {
  const view = scene?.cameras?.main?.worldView;
  const margin = hexSize * marginFactor;
  return !view || (
    x >= view.x - margin && x <= view.right + margin && y >= view.y - margin && y <= view.bottom + margin
  );
};

/**
 * Отряды погибают красиво: редьюсер уже убрал их с поля и записал в итоги боя, поэтому на
 * месте каждого остается двойник (см. buildUnitDeathCastPlan). Итоги боя ждут конца
 * анимации, если ее есть кому смотреть: погибшие вне кадра не рисуются и модалку не держат.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Array<{killedUnit: Object, combatType: string}>} combatResults
 * @returns {number} на сколько придержаны итоги боя, мс
 */
export const playUnitDeathEffects = (store, hexGridRenderer, combatResults) => {
  if (!checkIfAnimationAllowed(store, hexGridRenderer)) {
    return 0;
  }

  const {viewMarginFactor, staggerMs, maxStaggerMs, fullEffectsLimit, modalLeadMs} = UNIT_DEATH_CAST;
  const state = store.getState();
  const players = selectPlayers(state) || [];
  const deaths = (combatResults || [])
    .filter(checkIfDiedAtStageEnd)
    .map(({killedUnit}) => ({unit: killedUnit, anchor: hexGridRenderer.getAbilityCastAnchor(killedUnit?.hexKey)}))
    .filter(({anchor}) => anchor && checkIfUnitInView(hexGridRenderer.scene, anchor, viewMarginFactor));

  let holdMs = 0;
  deaths.forEach(({unit, anchor}, index) => {
    const owner = players.find((player) => player.id === unit.ownerId);
    const plan = buildUnitDeathCastPlan({
      unit: anchor,
      hexSize: anchor.hexSize,
      hexRotation: anchor.hexRotation,
      color: owner ? getPlayer0xColor(owner.color) : 0xffffff,
      art: hexGridRenderer.getUnitRecruitmentArt?.(unit, {players, state}),
      tier: resolveUnitRecruitmentTier(unit.type),
      delayMs: Math.min(index * staggerMs, maxStaggerMs),
      isReduced: deaths.length > fullEffectsLimit,
    });
    const animationId = hexGridRenderer.abilityCastAnimation.play(plan, {
      onImpact: () => hexGridRenderer.showBloodSplatter?.({
        at: anchor.body,
        direction: Math.random() * Math.PI * 2,
        hexSize: anchor.hexSize,
        factionId: unit.factionId,
        intensity: UNIT_DEATH_CAST.blood.intensity,
      }),
    });
    if (animationId !== null) {
      holdMs = Math.max(holdMs, plan.totalMs - modalLeadMs);
    }
  });

  if (holdMs > 0) {
    startUnitDeathPause(UNIT_DEATH_CAST.meleePauseMs);
    store.dispatch(startUnitDeathAnimation());
    clearTimeout(modalReleaseTimeoutId);
    modalReleaseTimeoutId = setTimeout(() => store.dispatch(finishUnitDeathAnimation()), holdMs);
  }
  return holdMs;
};
